import { Box, Button, Divider, TextField, Typography } from '@mui/material';
import React, { Dispatch, SetStateAction, useState } from 'react';
import { Sidebar } from '../../../StoryList/Sidebar';
import CopyToClipboard from 'react-copy-to-clipboard';
import moment from 'moment';
import {
  IExtendedStoryPrivilege,
  IPublicationViewMessages,
  ISessionUser,
  IStoriesFolder,
  IStoryListMessages,
  IStoryListSelection,
  ISubjectClaims
} from '../../../../../types';
import { IPublication, IPublicationNavMenuItem } from '@opengov/stories-shared/types';
import { PUBLICATIONS_MANAGER_PRIVILEGE, STATE_PUBLISHED } from '@opengov/stories-shared/constants/stories';
import { modals as modalMessages, onlinePublication as onlinePublicationMessages } from '../../../../../locale/stories.en.locale.json';
import { DATE_FORMAT } from '../../../../constants/publications';
import { renderStatusColumn } from '../../PublicationList/PublicationList';
import { useToast } from '../../../Toast/context/useToast.context';
import { useNavigationMenus } from '../../OnlinePublication/hooks/useNavigationMenus';
import { generateOnlinePublicationUrl } from '../../../../utils/generateOnlinePublicationUrl';
import { SetupOnlinePublicationWarning } from '../../../../../modals/components/SetupOnlinePublicationWarning/SetupOnlinePublicationWarning';
import { styles } from './PublicationSidebarContent.styles';

type IPublicationSidebarContent = {
  messages: IPublicationViewMessages;
  publication: IPublication;
  copyPublicationJobId: string;
  selectedSectionId: string;
  entityId: number;
  isFoldersLoading: boolean;
  publicationFolderStructure: IStoriesFolder[];
  isAdmin: boolean;
  storyMessages: IStoryListMessages;
  entitySubdomain: string;
  displayReportEmbeds: boolean;
  selectedSectionLabel: string;
  user: ISessionUser;
  enableGoogleTranslateForPublications: boolean;
  storiesAccessControlEnabled: boolean;
  currentSubjectClaims: ISubjectClaims | null;
  currentSubjectPublicationStoryClaim: IExtendedStoryPrivilege | null;
  isCustomSpreadsheetReportEnabled: boolean;
  setSelectedSection: (section: IStoryListSelection, viewtype?: string) => void;
  setIsCreatePublicationModalVisible: Dispatch<SetStateAction<boolean>>;
  exportPublicationPdfInlineMessages: () => JSX.Element[];
};

export const PublicationSidebarContent = (props: IPublicationSidebarContent): JSX.Element => {
  const {
    publication,
    messages,
    copyPublicationJobId,
    selectedSectionId,
    entityId,
    isFoldersLoading,
    publicationFolderStructure,
    isAdmin,
    storyMessages,
    entitySubdomain,
    displayReportEmbeds,
    selectedSectionLabel,
    user,
    enableGoogleTranslateForPublications,
    storiesAccessControlEnabled,
    currentSubjectClaims,
    currentSubjectPublicationStoryClaim,
    isCustomSpreadsheetReportEnabled,
    setSelectedSection,
    setIsCreatePublicationModalVisible,
    exportPublicationPdfInlineMessages
  } = props;

  const [isGeneratingLink, setIsGeneratingLink] = useState<boolean>(false);
  const [isSetupOnlinePublicationWarningOpen, setIsSetupOnlinePublicationWarningOpen] = useState(false);
  const { toggleToast } = useToast();

  // Use live menus for the update-link gate.
  const navMenusQuery = useNavigationMenus(
    publication?.id ? { publicationId: publication.id } : null,
    ''
  );
  const liveNavigationMenuItems = navMenusQuery.data ?? [];

  const openTab = () => {
    window.open(publication.publicUrl, '_blank');
  };

  const storiesInPublicationNavMenuItems = liveNavigationMenuItems?.reduce(
    (storiesCount: number, menuItem: IPublicationNavMenuItem) =>
      storiesCount + (menuItem?.publicationNavigationMenuStories?.length ?? 0),
    0
  );

  const generateSharePublicationUrl = () => {
    if (liveNavigationMenuItems.length && storiesInPublicationNavMenuItems) {
      setIsGeneratingLink(true);
      void generateOnlinePublicationUrl({
        publication,
        userId: user.id,
        update: true,
        enableGoogleTranslateForPublications,
        isPreview: false,
        entitySubdomain,
        notifyAdmins: true
      })
        .then(() => {
          setIsGeneratingLink(false);
          toggleToast({
            title: onlinePublicationMessages.linkUpdated,
            body: onlinePublicationMessages.nowYouCanCopy,
            severity: 'success'
          });
        })
        .catch(() => {
          setIsGeneratingLink(false);
          toggleToast({
            title: onlinePublicationMessages.somethingWentWrongError,
            body: onlinePublicationMessages.failedToGenerateTheLink,
            severity: 'error'
          });
        });
    } else {
      setIsSetupOnlinePublicationWarningOpen(true);
    }
  };

  return (
    <Box sx={styles.sidebarContent}>
      <Box component="section" aria-label="Publication summary" sx={styles.publicationSummary}>
        {publication.description && (
          <Box sx={styles.publicationTextSummary}>
            <Typography color="text.secondary">
              {publication.description}
            </Typography>
          </Box>
        )}
        <Box data-test="publication-created-date" sx={styles.publicationTextSummary}>
          <Typography color="text.secondary">
            {publication.createdAt &&
              `${messages.created} ${moment.utc(publication.createdAt).local().format(DATE_FORMAT)}`}
          </Typography>
        </Box>
        <Box sx={styles.publicationTextSummary}>
          <Typography color="text.secondary">
            {publication.updatedAt &&
              `${messages.lastUpdated} ${moment.utc(publication.updatedAt).local().format(DATE_FORMAT)}`}
          </Typography>
        </Box>
        {!!publication?.id && (
          <Box sx={styles.publicationStatus}>
            <Typography sx={styles.statusLabel}>
              {messages.onlinePublicationStatus}
            </Typography>
            {renderStatusColumn({ value: publication?.status })}
          </Box>
        )}
        {publication.publicUrl && publication?.status === STATE_PUBLISHED && (
          <Box sx={styles.mt16}>
            <TextField
              id="publicationURL"
              label={messages.onlinePublicationURL}
              value={publication.publicUrl}
              inputProps={{ 'data-test': 'publication-url', readOnly: true, className: 'no-format' }}
              size="small"
              sx={styles.urlField}
            />
            <Box sx={styles.buttons}>
              <CopyToClipboard text={publication.publicUrl}>
                <Button variant="outlined" color="secondary" size="small" data-test="copy-share-publication-link">
                  {messages.copyLink}
                </Button>
              </CopyToClipboard>
              <Button variant="outlined" color="secondary" size="small" onClick={openTab} data-test="open-in-newtab-online-publication-link">
                {messages.view}
              </Button>
              {isAdmin && currentSubjectPublicationStoryClaim?.includes(PUBLICATIONS_MANAGER_PRIVILEGE) && (
                <Button
                  variant="contained"
                  size="small"
                  disabled={isGeneratingLink}
                  onClick={generateSharePublicationUrl}
                  data-test="update-link-online-publication-link"
                >
                  {messages.updateLink}
                </Button>
              )}
            </Box>
          </Box>
        )}
        {exportPublicationPdfInlineMessages()}
      </Box>
      <Box sx={styles.divider}>
        <Divider />
      </Box>
      {!!publication.id && (
        <Sidebar
          copyPublicationJobId={copyPublicationJobId}
          isPublicationsTabSelected={true}
          publicationViewMessages={messages}
          currentFolderId={selectedSectionId}
          entityId={entityId}
          isPublicationFoldersLoading={isFoldersLoading}
          folderStructure={publicationFolderStructure}
          isAdmin={isAdmin}
          messages={storyMessages}
          rootFolderId={selectedSectionId}
          entitySubdomain={entitySubdomain}
          setSelectedSection={setSelectedSection}
          setIsCreatePublicationModalVisible={setIsCreatePublicationModalVisible}
          showReports={displayReportEmbeds}
          selectedSectionName={selectedSectionLabel}
          storiesAccessControlEnabled={storiesAccessControlEnabled}
          currentSubjectClaims={currentSubjectClaims}
          currentSubjectPublicationStoryClaim={currentSubjectPublicationStoryClaim}
          isCustomSpreadsheetReportEnabled={isCustomSpreadsheetReportEnabled}
        />
      )}
      <SetupOnlinePublicationWarning
        open={isSetupOnlinePublicationWarningOpen}
        onClose={() => setIsSetupOnlinePublicationWarningOpen(false)}
        messages={modalMessages}
      />
    </Box>
  );
};
